// ─────────────────────────────────────────────────────────────────────────────
// ActivityPanel — the ACTIVITY feature front end (JOINT-signed v4, build step 1).
//
// The three-source WHO/WHEN timestamped index — her chain's floor, visible.
// INDEX-ONLY BOARD (signed H2): rows carry who/when/where/subject-label + the
// four-state chip. No content, no transcript text, no what/why/how on rows,
// ever. Drill-in is a separate authed call showing the payload atom.
//
// Part-1 data source is fixtures (activityFixtures.ts — the UI defines the API
// contract). Part 2 flips USE_FIXTURES to false and the already-written fetch
// path goes live against GET /api/rexy/activity + /api/rexy/activity/detail.
//
// Palette: REXY_CONSOLE only (cyan #00BFFF / dark), dimensional card
// idiom carried from DashboardColumns/TelemetryCard.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import type { ActivityDetail, ActivityIndexResponse, ActivityRow } from './activityFixtures';
import {
  useActivity,
  REXY_LANE,
  ACTIVITY_KIND_LABEL as KIND_LABEL,
  DETAIL_FIELDS,
  hhmm,
  nextPulseHour,
  pulseOutcome,
} from '../../data/dashboard';
import type { ActivityLane } from '../../data/dashboard';
import { useCollapsed, PanelTitle } from './PanelCollapse';

const MONO = REXY_FONT;

// DESKTOP ARRANGEMENT ONLY. The two endpoints, the 30s poll, the Part-1 fixture
// seam and the drill-in GATE (one row expanded at a time, shared detail cache,
// live pending→read fill-in) moved to data/dashboard/useActivity.ts under the
// 2026-07-31 "one core, every window" ruling. The render below is unchanged.

const CHIPS: Record<ActivityRow['status'], { text: string; color: string }> = {
  pending: { text: '● pending', color: REXY_CONSOLE.warning },
  read: { text: '✓ read', color: REXY_CONSOLE.primary },
  absent: { text: '○ absent', color: REXY_CONSOLE.dimText },
  unknown: { text: '· unknown', color: REXY_CONSOLE.mutedText },
};


export interface ActivityPanelProps {
  /**
   * WHICH LANE THIS MOUNT READS. Defaults to Rexy's, so `<ActivityPanel />` at
   * DashboardColumns.tsx:143 is unchanged — the same component, the same
   * endpoints, the same render. GROWTH ▸ CEO passes `GROWTH_LANE`.
   */
  lane?: ActivityLane;
  /**
   * FALSE INSIDE MISSION CONTROL, and it is not a style preference. MC's own
   * `Panel` already draws the section, the title, the note and the badge, so a
   * second card inside it is a card in a card. Unframed drops THIS component's
   * card and its `║ ACTIVITY` header — nothing else. The strap stays, because
   * `updated HH:MM:SS` is the liveness proof and this surface exists to be
   * checked live.
   */
  framed?: boolean;
  /**
   * Probe handle, and it is OPT-IN ON PURPOSE.
   *
   * Defaulting it would have put `data-testid` and `data-lane` onto HER panel
   * too — two attributes she did not have, on the mount that must stay
   * byte-identical. React omits an attribute whose value is `undefined`, so a
   * call site that passes nothing renders exactly the DOM it rendered before,
   * and `activityPanel.parity.test` hashes both to prove it rather than assert
   * it.
   */
  testId?: string;
  /**
   * The homepage mount's fold control and its stored choice (PanelCollapse). Opt-in:
   * Mission Control passes none, so its mount renders exactly as before.
   */
  collapseKey?: string;
}

export default function ActivityPanel(
  { lane = REXY_LANE, framed = true, testId, collapseKey }: ActivityPanelProps = {},
) {
  const {
    index, err, updatedAt, expandedRef, details, toggleRow,
  } = useActivity(lane);
  const [collapsed, toggle] = useCollapsed(collapseKey);

  function renderExpansion(row: ActivityRow, nextPulses: string[]) {
    if (row.status === 'pending') {
      return (
        <div style={styles.expansion}>
          <div style={styles.expansionNote}>
            not yet read {'—'} next pulse {nextPulseHour(nextPulses)}
          </div>
        </div>
      );
    }
    if (row.status === 'unknown') {
      return (
        <div style={styles.expansion}>
          <div style={styles.expansionNote}>no status recorded (pre-schema payload)</div>
        </div>
      );
    }
    const slot = details[row.journal_ref];
    if (!slot || slot.state === 'loading') {
      return (
        <div style={styles.expansion}>
          <div style={styles.expansionNote}>loading{'…'}</div>
        </div>
      );
    }
    if (slot.state === 'error') {
      return (
        <div style={styles.expansion}>
          <div style={styles.expansionNote}>not_available {'—'} {slot.error}</div>
        </div>
      );
    }
    const d = slot.detail;
    return (
      <div style={styles.expansion}>
        {DETAIL_FIELDS.map((f) =>
          d[f] === undefined || f === 'journal_ref' ? null : (
            <div key={f} style={styles.detailRow}>
              <span style={styles.detailKey}>{f}</span>
              <span style={styles.detailVal}>{f === 'when' ? hhmm(String(d[f])) : String(d[f])}</span>
            </div>
          )
        )}
      </div>
    );
  }

  return (
    <div
      style={framed ? styles.card : styles.bare}
      data-testid={testId}
      data-lane={testId ? lane.key : undefined}
    >
      <div style={collapsed ? { ...styles.headerRow, marginBottom: 0 } : styles.headerRow}>
        {framed ? (
          <PanelTitle title="ACTIVITY" style={styles.sectionHeader} collapseKey={collapseKey}
                      collapsed={collapsed} onToggle={toggle} />
        ) : null}
        <div style={styles.subtitle}>
          three sources {'·'} who/when
          {updatedAt ? <span> {'·'} updated {updatedAt}</span> : null}
        </div>
      </div>

      {collapsed ? null : err ? (
        <div style={styles.degraded}>not_available {'—'} {err}</div>
      ) : !index ? (
        <div style={styles.degraded}>loading{'…'}</div>
      ) : index.groups.length === 0 ? (
        <div style={styles.degraded}>no OS activity in this window</div>
      ) : (
        index.groups.map((g, gi) => (
          <div key={g.slot_id}>
            {/* Group header — thin divider with slot label + pulse outcome;
                the newest/current group carries the next-pulse marker. */}
            <div style={styles.groupHeader}>
              <span style={styles.groupLabel}>{'─'} {g.label}</span>
              <span style={styles.groupLine} />
              <span style={styles.groupPulse}>
                {gi === 0
                  ? `next pulse ${nextPulseHour(index.next_pulses)}`
                  : g.pulse
                  ? pulseOutcome(g.pulse)
                  : ''}
                {' ─'}
              </span>
            </div>

            {g.rows.length === 0 ? (
              <div style={styles.emptyGroup}>no events this hour</div>
            ) : (
              g.rows.map((row) => {
                const chip = CHIPS[row.status];
                const subjectLabel =
                  row.count !== undefined ? `${row.subject} ×${row.count}` : row.subject;
                return (
                  <div key={row.journal_ref}>
                    <div
                      style={styles.itemRow}
                      onClick={() => toggleRow(row)}
                      title={row.journal_ref}
                    >
                      <span style={styles.rowTime}>{hhmm(row.ts)}</span>
                      <span style={styles.rowWho}>{row.who}</span>
                      <span style={styles.rowWhere}>
                        {row.domain} {'·'} {KIND_LABEL[row.source_kind]} {'·'} {row.locus}
                      </span>
                      <span style={styles.rowSubject}>{subjectLabel}</span>
                      <span style={{ ...styles.chip, color: chip.color }}>{chip.text}</span>
                    </div>
                    {expandedRef === row.journal_ref && renderExpansion(row, index.next_pulses)}
                  </div>
                );
              })
            )}
          </div>
        ))
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  // UNFRAMED — no border, no background, no shadow, no padding. Mission
  // Control's `Panel` supplies all four, and the rows below are identical
  // either way: the frame is the only thing this switch touches.
  bare: { minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' },
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    // Dimensional, never flat (CLAUDE.md UI §3) — same bubble idiom as DashboardColumns.
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  // Title sits at the TOP of the panel; the meta strap sits UNDER it and wraps, so it
  // can never displace the title or overflow the card edge (owner ruling 2026-07-24).
  headerRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 3,
    marginBottom: 10,
    minWidth: 0,
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
  },
  subtitle: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    color: REXY_CONSOLE.dimText,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    maxWidth: '100%',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    marginBottom: 2,
  },
  groupLabel: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    color: REXY_CONSOLE.mutedText,
    whiteSpace: 'nowrap',
  },
  groupLine: {
    flex: 1,
    borderTop: `1px solid ${REXY_CONSOLE.divider}`,
  },
  groupPulse: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    color: REXY_CONSOLE.dimText,
    whiteSpace: 'nowrap',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    padding: '3px 4px',
    borderBottom: `1px solid ${REXY_CONSOLE.rowDivider}`,
    cursor: 'pointer',
  },
  rowTime: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.mutedText, whiteSpace: 'nowrap' },
  rowWho: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.primary, whiteSpace: 'nowrap' },
  rowWhere: {
    fontFamily: MONO,
    fontSize: 9,
    color: REXY_CONSOLE.dimText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    flex: 1,
  },
  rowSubject: {
    fontFamily: MONO,
    fontSize: 9,
    color: REXY_CONSOLE.infText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    maxWidth: '38%',
  },
  chip: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
  },
  emptyGroup: {
    fontFamily: MONO,
    fontSize: 9,
    color: REXY_CONSOLE.dimText,
    padding: '3px 4px',
  },
  expansion: {
    margin: '2px 4px 6px 4px',
    padding: '6px 8px',
    borderRadius: 4,
    border: `1px solid ${REXY_CONSOLE.divider}`,
    background: REXY_CONSOLE.panel,
  },
  expansionNote: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText },
  detailRow: { display: 'flex', gap: 8, alignItems: 'baseline', padding: '1px 0' },
  detailKey: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    color: REXY_CONSOLE.mutedText,
    minWidth: 56,
  },
  detailVal: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.infText, lineHeight: 1.4 },
};
